import { useEffect, useRef, useState } from 'react';
import { logger } from 'packages/logger';
import { ScrollableModal } from 'tradera/components/scrollable-modal/scrollable-modal';
import type { IFormInputs } from 'tradera/components/messaging/messaging-new-message-form';
import { NewMessageForm } from 'tradera/components/messaging/messaging-new-message-form';
import { NewMessageHeader } from 'tradera/components/messaging/messaging-new-message-header';
import { isSwedenCountryCodeOrUndefined } from 'tradera/localization/countrycode';
import { useAppSelector } from 'tradera/state/hooks';
import { selectMemberBirthdate } from 'tradera/state/member/selectors';
import { useTranslator } from 'tradera/lang/translate';
import { InfoBox } from 'tradera/components/info-box/info-box';
import type { SubmitHandler } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
    useAddMessageMutation,
    useGetProfileQuery,
    useInitiateThreadMutation
} from 'tradera/components/messaging/firebase-api';
import { CONSTANTS } from 'tradera/apps/tradera/messaging/constants';
import { showErrorToast, showSuccessToast } from 'tradera/services/toasts';
import { BankIdModal } from 'tradera/components/bank-id/bank-id-modal';
import { startAuthAndOpenModal } from 'tradera/components/bank-id/state/bank-id/actions';
import type { AnyAction } from 'redux';
import { LocalLoader } from 'tradera/components/loader/loader';
import BankIdInfoModal from 'tradera/components/bank-id/bank-id-info-modal';
import type { InitiateThreadResponse } from 'tradera/components/messaging/firebase.types';
import {
    getRecipient,
    getThreadInteractivity
} from 'tradera/apps/tradera/messaging/utils/thread';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { openModal } from 'tradera/state/ui/reducer';
import { headerInternationalIdentificationModalId } from 'tradera/components/international-identification-modal/header-international-identification-modal';
import { timestampToDate } from 'tradera/apps/tradera/messaging/utils/firebase-timestamp';

type NewMessageModalProps = {
    onModalClosed: () => void;
    title: string;
    itemId: number;
    senderId: number;
    recipientId: number;
    recipientAlias: string;
    itemTitle: string;
    itemImageUrl: string;
    itemSellerId?: number;
    purchaseOrderId?: string;
};

const parseErrorMessageIntoTranslationKey = (
    errorMessage: string | null
): string => {
    const defaultErrorKey = 'messaging_errors_not_allowed_start_conversation';
    if (!errorMessage) {
        return defaultErrorKey;
    }

    switch (errorMessage) {
        case CONSTANTS.ERRORS.MEMBER_BLOCKED:
            return 'messaging_error_member_blocked';
        case CONSTANTS.ERRORS.BLOCKED_BY_MEMBER:
            return 'messaging_error_blocked_by_member';
        case CONSTANTS.ERRORS.MEMBER_NOT_CONTACTABLE:
            return 'messaging_error_member_not_contactable';
        default:
            return 'messaging_errors_not_allowed_start_conversation';
    }
};

export const NewMessageModal = ({
    onModalClosed,
    title,
    itemId,
    senderId,
    recipientId,
    recipientAlias,
    itemTitle,
    itemImageUrl,
    itemSellerId,
    purchaseOrderId
}: NewMessageModalProps) => {
    const { t } = useTranslator();
    const dispatch = useDispatch();
    const memberBirthdate = useSelector(selectMemberBirthdate);
    const [isInitialLoadingOfModalDone, setIsInitialLoadingOfModalDone] =
        useState(false);
    const [isIdVerificationError, setIsIdVerificationError] = useState(false);
    const [isPossibleToAddMessage, setIsPossibleToAddMessage] = useState(true);
    const [
        notPossibleToAddMessageErrorText,
        setNotPossibleToAddMessageErrorText
    ] = useState('');

    const [
        initiateThread,
        { data: initiateThreadData, isError: isInitiateThreadError }
    ] = useInitiateThreadMutation();

    const {
        data: firebaseProfileQuery,
        isError: isGetProfileError,
        isFetching: isGetProfileFetching,
        isUninitialized: isGetProfileUninitialized
    } = useGetProfileQuery(undefined, {
        refetchOnMountOrArgChange: true,
        skip: !initiateThreadData?.result || !isPossibleToAddMessage
    });

    const [addMessage, { isError: isAddMessageError }] =
        useAddMessageMutation();

    //Needed because of an bug in RTK Query in current version where if skip is set to true the current data is cleared
    useEffect(() => {
        if (
            !isInitialLoadingOfModalDone &&
            (!isGetProfileUninitialized || !isPossibleToAddMessage)
        ) {
            setIsInitialLoadingOfModalDone(true);
        }
    }, [
        isAddMessageError,
        isGetProfileUninitialized,
        isInitialLoadingOfModalDone,
        isPossibleToAddMessage
    ]);

    useEffect(() => {
        if (firebaseProfileQuery?.result?.messageThrottledUntil) {
            if (
                timestampToDate(
                    firebaseProfileQuery.result.messageThrottledUntil
                ) > new Date()
            ) {
                setIsPossibleToAddMessage(false);
                setNotPossibleToAddMessageErrorText(
                    t('messaging_warnings_throttled')
                );
            }
        }
    }, [senderId, firebaseProfileQuery?.result, t]);

    // We initiate a thread without saving it to check if the user is allowed to start a thread or send a message in an existing thread,
    // some errors are thrown from the server and some needs to be checked in the response
    const isInitiateThreadVerificationDone = useRef(false);
    useEffect(() => {
        if (!isInitiateThreadVerificationDone.current) {
            isInitiateThreadVerificationDone.current = true;
            initiateThread({
                data: {
                    listingId: itemId.toString(),
                    senderId: senderId.toString(),
                    receiverId: recipientId.toString(),
                    persist: false
                }
            })
                .unwrap()
                .then((initiateThreadResponse) => {
                    const thread = initiateThreadResponse.result;
                    const recipient = getRecipient(
                        thread,
                        senderId.toString(),
                        t
                    );

                    if (recipient === null) {
                        setIsPossibleToAddMessage(false);
                        setNotPossibleToAddMessageErrorText(
                            t('messaging_errors_not_allowed_start_conversation')
                        );

                        return initiateThreadResponse;
                    }

                    const result = getThreadInteractivity(
                        thread,
                        recipient,
                        senderId.toString(),
                        null,
                        t
                    );

                    setIsPossibleToAddMessage(result.isAddMessageAllowed);
                    setNotPossibleToAddMessageErrorText(
                        result.addMessageText || ''
                    );

                    return initiateThreadResponse;
                })
                .catch((ex) => {
                    setIsPossibleToAddMessage(false);
                    const translationKey = parseErrorMessageIntoTranslationKey(
                        ex.data?.error?.message
                    );
                    setNotPossibleToAddMessageErrorText(t(translationKey));
                    return null;
                });
        }
    }, [initiateThread, itemId, recipientId, senderId, t]);

    const handleSubmit: SubmitHandler<IFormInputs> = async (formData) => {
        const initiateThreadResult = (await initiateThread({
            data: {
                listingId: itemId.toString(),
                senderId: senderId.toString(),
                receiverId: recipientId.toString(),
                persist: true
            }
        })
            .unwrap()
            .catch((ex) => {
                console.log(ex);
                showErrorToast('messaging_new_message_modal_errorToastText');
                return null;
            })) as InitiateThreadResponse | null;

        if (initiateThreadResult) {
            await addMessage({
                data: {
                    threadId: initiateThreadResult.result?.threadId ?? '',
                    type: CONSTANTS.FIREBASE.MESSAGE_TYPE.TEXT,
                    message: formData.messageText
                }
            })
                .unwrap()
                .then(() => {
                    onModalClosed();
                    return showSuccessToast(
                        'messaging_new_message_modal_successToastText'
                    );
                })
                .catch((ex) => {
                    console.log(ex);
                    return showErrorToast(
                        'messaging_new_message_modal_errorToastText'
                    );
                });
        }
    };

    //Check if Id verification is required
    const [showBankIdInfoModal, setShowBankIdInfoModal] = useState(false);
    const [bankIdFlowInProgress, setBankIdFlowInProgress] = useState(false);
    const [
        internationalVerificationInProgress,
        setInternationalVerificationInProgress
    ] = useState(false);
    const countryCodeIso2 = useAppSelector(selectShippingRegionCountryCodeIso2);
    useEffect(() => {
        if (
            !isGetProfileFetching &&
            firebaseProfileQuery?.result.requireIdentityVerification
        ) {
            if (isSwedenCountryCodeOrUndefined(countryCodeIso2)) {
                setShowBankIdInfoModal(true);
            } else {
                setShowBankIdInfoModal(false);
                setInternationalVerificationInProgress(true);
                dispatch(
                    openModal({
                        modalId: headerInternationalIdentificationModalId,
                        data: { purpose: 'Messaging' }
                    })
                );
            }
        } else {
            setShowBankIdInfoModal(false);
            setInternationalVerificationInProgress(false);
        }
    }, [
        firebaseProfileQuery?.result.requireIdentityVerification,
        countryCodeIso2,
        isGetProfileFetching,
        dispatch
    ]);

    //BankId identity verification for swedish users
    const verifyWithBankIdAction = () => {
        setShowBankIdInfoModal(false);
        setBankIdFlowInProgress(true);
        dispatch(
            startAuthAndOpenModal(
                () => {
                    setIsIdVerificationError(false);
                    setBankIdFlowInProgress(false);
                },
                () => {
                    logger('problems with VIP add message bankid verify');
                    setIsIdVerificationError(true);
                    setBankIdFlowInProgress(false);
                },
                'vip-add-message'
            ) as unknown as AnyAction
        );
    };

    const isIdVerificationNotRequiredOrDone =
        !showBankIdInfoModal &&
        !bankIdFlowInProgress &&
        !internationalVerificationInProgress &&
        !isIdVerificationError;

    return (
        <>
            {showBankIdInfoModal && (
                <BankIdInfoModal
                    birthdate={memberBirthdate || ''}
                    onCloseAction={() => {
                        setShowBankIdInfoModal(false);
                        onModalClosed();
                    }}
                    onVerifyWithBankId={verifyWithBankIdAction}
                    text={t('messaging_new_message_modal_bankIdText')}
                />
            )}
            {bankIdFlowInProgress && <BankIdModal />}

            {isIdVerificationNotRequiredOrDone && (
                <ScrollableModal
                    show={true}
                    maxWidth="530px"
                    onClose={onModalClosed}
                    title={title}>
                    <NewMessageHeader
                        recipientId={recipientId}
                        recipientAlias={recipientAlias}
                        itemTitle={itemTitle}
                        itemImageUrl={itemImageUrl}
                        purchaseOrderId={purchaseOrderId}
                        itemSellerId={itemSellerId}
                    />
                    {!isInitialLoadingOfModalDone && (
                        <LocalLoader className="z-default" />
                    )}
                    {!isPossibleToAddMessage && (
                        <InfoBox info>
                            {notPossibleToAddMessageErrorText}
                        </InfoBox>
                    )}
                    {isPossibleToAddMessage && (
                        <NewMessageForm
                            isError={
                                isGetProfileError ||
                                isAddMessageError ||
                                isInitiateThreadError ||
                                isIdVerificationError
                            }
                            onSubmit={handleSubmit}
                            onClose={onModalClosed}
                        />
                    )}
                </ScrollableModal>
            )}
        </>
    );
};
